import type {
  AssignedVehicle,
  VehicleAssignment,
  VehicleDocument,
  VehicleLifecycleStatus,
  VehicleRecord,
} from "./driver-api.ts";
import type { ChipClass } from "./shift-readiness.ts";
import type { CheckProps } from "./ui/PhoneShell.tsx";

/**
 * `Vehicle` — the courier's own machine and its papers, as the design draws it.
 *
 * The design's screen, value for value from `15 WP14 Driver Onboarding and Earnings.dc.html`:
 *
 *     vehicle:{l:'Vehicle',art:'idle',on:0,chip:['done','active_record'],
 *       title:'Motorcycle T 482 DKX',sub:'Honda CB150 · 2022 · red',
 *       checks:[['ok','description','Registration','Valid to Mar 2027'],
 *               ['ok','shield','Insurance','Valid to 14 Nov 2026'],
 *               ['warn','build','Roadworthiness','Expires in 12 days · renew soon']],
 *       btns:[['Upload new roadworthiness','upload',1],['Add another vehicle','add',0]]}
 *
 * Every one of those has a source now, and did not until
 * [ADR 0034](../../../docs/adr/0034-a-drivers-vehicle-is-an-assignment-record-many-over-time.md).
 * `tests/contract/mobile-screen-support.test.ts` marks this screen **buildable with nothing
 * missing** — the only one of the eighteen whose every absence a single decision closed.
 *
 * ## Why the plurality is in the read and in one button, and nowhere else on the screen
 *
 * ADR 0034 chose an assignment record over a `vehicleId` column **because this screen draws
 * `Add another vehicle`**: a one-vehicle model has to be rebuilt the first time a courier changes
 * machine, and a column on the vehicle answers "who is driving this" and can never answer "who was
 * driving it on 3 October", which is the question a disputed trip asks.
 *
 * So the read is a list — `GET /v1/drivers/{driverId}/vehicle-assignments`, newest first, each row
 * carrying its own `status: active | ended` and its own `effectiveFrom`/`effectiveTo` — and the
 * screen draws the **active** row out of it. What it does **not** do is draw the others, and that is
 * the design rather than a shortcut: the design draws one vehicle, three document rows and two
 * buttons, and `CLAUDE.md` section 1 forbids adding a section that is not in the design as plainly
 * as it forbids removing one. The plurality is expressed by the three things the design does draw —
 * a list read rather than a field, a row chosen by its own lifecycle, and the button that adds
 * another — and the question of where a courier would *see* their previous vehicles is recorded as
 * Q-122 rather than answered by inventing a panel.
 *
 * ## The two reads, and the one that stopped being over-scoped
 *
 * The assignment row carries the plate, the class, the make, the model, the colour and the year, so
 * the title and the sub-line come off one read. The **documents** are only on the vehicle itself, so
 * the three check rows need `GET /v1/vehicles/{vehicleId}` keyed on the `vehicle.vehicleId` the
 * first read publishes.
 *
 * R-48 recorded the second as over-scoped: bounded by the verified tenant and by nothing else, where
 * the assignment read beside it has a `:self` arm, so a courier's token holding `vehicle:read` read
 * every vehicle the tenant owns.
 * [ADR 0038](../../../docs/adr/0038-a-reads-audiences-are-the-ones-its-contract-publishes.md) closed
 * it — the read now has a `vehicle:self` arm bounded by the assignment's stored driver subject and
 * refusing with a 404 — so this screen spends the owner scope and the handset is no longer granted
 * the fleet one. What it reads is unchanged, and so is the workaround's effect: it was already
 * reading only the vehicle its own assignment names. R-48, R-51.
 */

/** The scope the assignment read spends. Its `:self` arm is written into the route. ADR 0034. */
export const VEHICLE_ASSIGNMENT_SCOPE = "vehicle-assignment:self";

/** The scope the vehicle read spends. Its `:self` arm is written into the route. ADR 0038. */
export const VEHICLE_READ_SCOPE = "vehicle:self";

/**
 * The assignment this screen is about: the active one, newest first.
 *
 * `status` rather than `effectiveTo`, because the contract publishes the word and deriving it from
 * the date would be this application re-deciding what "active" means — and a row ended with an
 * `effectiveTo` in the future is exactly the case the two would disagree on.
 *
 * `find` over the page in the order the service returned it, which the contract states is newest
 * first. A vehicle holds at most one active assignment and a driver may hold several, so there can
 * legitimately be more than one active row here; the newest is the one the design's single headline
 * is about, and Q-122 records that the design draws nowhere to show the second.
 */
export function activeAssignment(
  assignments: readonly VehicleAssignment[],
): VehicleAssignment | undefined {
  return assignments.find((assignment) => assignment.status === "active");
}

/**
 * `Motorcycle T 482 DKX` — the class and the registration.
 *
 * `vehicleClass` is a free string of up to 120 characters and the design prints it capitalised, so
 * the first letter is upper-cased here. That is this build's and is flagged on the screen when the
 * service's own value differed: a class stored as `motorcycle` would otherwise print
 * `motorcycle T 482 DKX`, and a class stored as `MOTORCYCLE` is left alone rather than
 * title-cased, because re-casing a value a tenant typed is editing it. Q-121.
 */
export function vehicleTitle(vehicle: AssignedVehicle): string {
  return `${sentenceCase(vehicle.vehicleClass)} ${vehicle.registrationNumber}`;
}

/** True when the class is printed exactly as DRV-04 published it, so the flag can say when not. */
export function classIsVerbatim(vehicle: AssignedVehicle): boolean {
  return sentenceCase(vehicle.vehicleClass) === vehicle.vehicleClass;
}

/**
 * `Honda CB150 · 2022 · red` — the make and model, the year and the colour.
 *
 * All four are **optional** on DRV-04's record, and the design's sub-line is the only place any of
 * them appears. So the line is composed from what is published and says what is not: with every
 * field present it is the design's sentence word for word, and with none of them it is a sentence
 * naming the absence rather than an empty line or a row of dashes. There is no middle treatment
 * that invents anything — a missing colour simply does not appear, and the flag says the words are
 * no longer the design's.
 *
 * The design's own separator and order are kept: `make model`, then the year, then the colour.
 */
export function vehicleSub(vehicle: AssignedVehicle): {
  readonly text: string;
  readonly designed: boolean;
} {
  const machine = [vehicle.make, vehicle.model].filter(isPresent).join(" ");
  const parts = [
    machine.length === 0 ? undefined : machine,
    vehicle.modelYear === undefined ? undefined : String(vehicle.modelYear),
    vehicle.colour,
  ].filter(isPresent);

  if (parts.length === 0) {
    return {
      text: "This vehicle's make, model, year and colour aren't recorded.",
      designed: false,
    };
  }
  const missing = [
    machine.length === 0 ? "make and model" : undefined,
    vehicle.modelYear === undefined ? "year" : undefined,
    vehicle.colour === undefined ? "colour" : undefined,
  ].filter(isPresent);
  return {
    text:
      missing.length === 0
        ? parts.join(" · ")
        : `${parts.join(" · ")} · no ${joinWithAnd(missing)} recorded`,
    designed: missing.length === 0,
  };
}

/**
 * The state chip. `['done','active_record']` is the design's, and the word is DRV-04's own.
 *
 * The label is `lifecycleStatus` verbatim — seven published values, of which the design prints one —
 * and the class is derived from the pair of statuses DRV-04's **own eligibility rule** reads: a
 * vehicle is refused if its `lifecycleStatus` is not `active_record`, or if its `operationalStatus`
 * is suspended or deactivated. So the screen reaches the same verdict the service does rather than a
 * second opinion about the same two fields, which is the reason the mobile sweep's `1 Go online`
 * element is pointed at `operationalStatus` and not at the plate.
 *
 * `suspended` and `deactivated` win over the lifecycle word, because a suspension after
 * confirmation is a refusal on top of an otherwise-good record and the chip has one line. A state is
 * never shown by colour alone: every branch below carries a word.
 */
export function vehicleChip(vehicle: AssignedVehicle): {
  readonly class: ChipClass;
  readonly label: string;
} {
  if (vehicle.operationalStatus === "suspended") return { class: "caution", label: "suspended" };
  if (vehicle.operationalStatus === "deactivated") return { class: "closed", label: "deactivated" };
  return { class: LIFECYCLE_CHIP[vehicle.lifecycleStatus], label: vehicle.lifecycleStatus };
}

/**
 * One chip class per published lifecycle value, total rather than defaulted.
 *
 * Total so that a value added to DRV-04's enum fails the build here instead of rendering as the
 * nearest confident neighbour. `active_record` is the design's `done`; the three unfinished states
 * take `waiting`, which is the design's own dotted ring and is what the onboarding board uses for a
 * step somebody else is holding; `rejected` takes `human`, the design's word for "you have
 * something to do"; and the two that must never be driven against take `closed`.
 */
const LIFECYCLE_CHIP: Readonly<Record<VehicleLifecycleStatus, ChipClass>> = {
  draft: "waiting",
  submitted: "waiting",
  under_review: "waiting",
  active_record: "done",
  rejected: "human",
  archived: "closed",
  merged: "closed",
};

/** True when the chip is the design's own `['done','active_record']`. */
export function chipIsDesigned(vehicle: AssignedVehicle): boolean {
  return vehicle.lifecycleStatus === "active_record" && vehicle.operationalStatus !== "suspended";
}

/**
 * How many days before an expiry the design's amber `renew soon` appears.
 *
 * **DRV-04 publishes no such window**, and this is not a number invented for the screen: it is
 * EXP-003's own `DOCUMENT_WARNING_WINDOW_MS`, whose default in
 * `services/experience/driver-bff/src/application/shift-readiness.ts` is fourteen days, and which
 * is what produces `expiringSoon` on the *courier's* documents on `7 Document expiring`. The
 * design's own sample — `Roadworthiness · Expires in 12 days · renew soon` — falls inside it, which
 * is the check that the borrowed number is the right one rather than merely a number.
 *
 * It is borrowed rather than owned, and the difference matters: EXP-003's is configurable per
 * deployment and this one is not, so a deployment that widened the courier's window would warn
 * about a licence earlier than about an insurance certificate. Recorded as Q-123. The fix is either
 * an `expiringSoon` flag on DRV-04's document or this window served through EXP-003's readiness
 * aggregate, and the second is the one ADR 0034 section 4 already points at.
 */
export const DOCUMENT_WARNING_DAYS = 14;

/**
 * The design's three document rows, in its order, with its captions.
 *
 * DRV-04 publishes five `documentType` values and the design draws three of them. The mapping is
 * the design's own words against the contract's own enum, and `Roadworthiness` is `inspection` —
 * which is what the service calls the thing a roadworthiness certificate is, and is also the name of
 * the `inspectionReferences` array it carried before documents had types at all.
 *
 * The two the design does not draw are drawn anyway, flagged, for the same reason `8 Proof` draws a
 * fourth thumbnail: a document a courier has uploaded and the platform has accepted must not vanish
 * from the screen that exists to show their papers. Removing it would be the more dangerous of the
 * two mistakes.
 *
 * **There is no glyph here, and that is the design's own doing rather than an omission.** Each of
 * the design's check rows is written as a four-element array whose second element is a glyph name —
 * `['ok','description','Registration','Valid to Mar 2027']` — and its own template then throws that
 * element away: `i: st==='ok' ? 'check_circle' : st==='bad' ? 'error' : st==='warn' ? 'warning' :
 * 'pending'`. So the rendered glyph is the **status**, on every check row of both design files, and
 * `description`, `shield` and `build` reach the page in neither. That is the same shape of fault as
 * the five map layers Q-68 records: a value stated in the file and overwritten before it is read.
 *
 * It was found by the pixel check and by nothing else. The first version of this file drew the
 * document glyphs, every value in it was read correctly out of the design, and the three rows were
 * wrong — which is exactly the case this repository keeps the comparison for. Q-130.
 */
const DOCUMENT_ROW: Readonly<
  Record<VehicleDocument["documentType"], { readonly title: string; readonly designed: boolean }>
> = {
  registration: { title: "Registration", designed: true },
  insurance: { title: "Insurance", designed: true },
  inspection: { title: "Roadworthiness", designed: true },
  permit: { title: "Permit", designed: false },
  other: { title: "Document", designed: false },
};

/**
 * The glyph the design's own template derives from a row's status, and nothing else.
 *
 * `15 WP14`'s four-status branch, exactly: a tick for `ok`, an error for `bad`, a warning for
 * `warn`, and `pending` for everything else. `src/shift-readiness.ts` reaches the same table for the
 * readiness screens' rows, which is what says this is the application's convention rather than this
 * screen's reading of it.
 */
function statusGlyph(status: CheckProps["status"]): string {
  switch (status) {
    case "ok":
      return "check_circle";
    case "bad":
      return "error";
    case "warn":
      return "warning";
    case "wait":
      return "pending";
  }
}

/** The design's own order, which is not the contract's enum order. */
const DESIGNED_TYPES = ["registration", "insurance", "inspection"] as const;

/**
 * The check rows, one per document the vehicle carries plus one per designed row it does not.
 *
 * Three decisions in here, each of which could have gone the convenient way:
 *
 *   - **A designed row with no document is drawn as an absence, not dropped.** The design draws
 *     three rows and nothing may be removed, so a vehicle with no insurance certificate recorded
 *     gets an `Insurance` row saying exactly that. A courier whose insurance row disappeared would
 *     conclude it was fine.
 *   - **Two documents of one type are two rows.** `PUT …/documents/{documentId}` is keyed on the
 *     document, so a vehicle can legitimately carry two insurance certificates — the lapsing one and
 *     its replacement. Drawing only the later one would hide a record the platform holds; the design
 *     happens to draw three of three different types, so on its own sample this makes no difference.
 *   - **`verificationStatus` is read before the expiry.** A certificate the platform has not
 *     accepted is not valid *whatever* its printed expiry says, and drawing
 *     `Valid to 14 Nov 2026` over a document DRV-04 rejected would be the worst sentence on this
 *     screen.
 *
 * `today` is the device's own date, because no tenant timezone is published anywhere on this
 * platform — Q-65, which `9 Earnings` and `7 Document expiring` already turn on.
 */
export function documentRows(
  documents: readonly VehicleDocument[],
  today: Date,
): readonly CheckProps[] {
  const rows: CheckProps[] = [];

  for (const type of DESIGNED_TYPES) {
    const held = documents.filter((document) => document.documentType === type);
    if (held.length === 0) {
      rows.push({
        key: `absent-${type}`,
        status: "wait",
        glyph: statusGlyph("wait"),
        title: DOCUMENT_ROW[type].title,
        sub: "Nothing is recorded against this vehicle",
        subIsAbsence: true,
      });
      continue;
    }
    for (const document of held) rows.push(documentRow(document, today));
  }

  for (const document of documents) {
    if (!DOCUMENT_ROW[document.documentType].designed) rows.push(documentRow(document, today));
  }

  return rows;
}

/** One document, as the design's check row. */
function documentRow(document: VehicleDocument, today: Date): CheckProps {
  const title = DOCUMENT_ROW[document.documentType].title;
  /** One row at one status, with the glyph the design's own template derives from it. */
  const at = (
    status: CheckProps["status"],
  ): { key: string; title: string; status: CheckProps["status"]; glyph: string } => ({
    key: document.documentId,
    title,
    status,
    glyph: statusGlyph(status),
  });

  if (document.verificationStatus === "rejected") {
    return { ...at("bad"), sub: "Not accepted · upload it again" };
  }
  if (document.verificationStatus === "pending") {
    return { ...at("wait"), sub: "Sent · we're checking it" };
  }
  if (document.expiresOn === undefined) {
    return {
      ...at("wait"),
      sub: "No expiry is recorded against this document",
      subIsAbsence: true,
    };
  }

  const days = wholeDaysUntil(document.expiresOn, today);
  if (days === undefined) {
    return {
      ...at("wait"),
      sub: "The recorded expiry can't be read as a date",
      subIsAbsence: true,
    };
  }
  if (days < 0) return { ...at("bad"), sub: `Expired ${longDate(document.expiresOn)}` };
  if (days === 0) return { ...at("warn"), sub: "Expires today · renew now" };
  if (days === 1) return { ...at("warn"), sub: "Expires tomorrow · renew now" };
  if (days <= DOCUMENT_WARNING_DAYS) {
    // The design's own sentence, word for word: `Expires in 12 days · renew soon`.
    return { ...at("warn"), sub: `Expires in ${String(days)} days · renew soon` };
  }
  // The design's own sentence: `Valid to 14 Nov 2026`.
  return { ...at("ok"), sub: `Valid to ${longDate(document.expiresOn)}` };
}

/**
 * True when every check row is one the design draws, with one of the design's own two sentences.
 *
 * Measured rather than assumed, so the screen's flag appears exactly when the words stop being the
 * design's — including the `Expires today` and `Expires tomorrow` branches, which exist because
 * `Expires in 1 days` is not English and the design has one sample of this sentence. Q-121.
 */
export function documentWordingIsDesigned(
  documents: readonly VehicleDocument[],
  today: Date,
): boolean {
  if (!documents.every((document) => DOCUMENT_ROW[document.documentType].designed)) return false;
  const rows = documentRows(documents, today);
  if (rows.length !== DESIGNED_TYPES.length) return false;
  return rows.every(
    (row) =>
      row.subIsAbsence !== true &&
      (/^Valid to /.test(row.sub) || /^Expires in \d+ days · renew soon$/.test(row.sub)),
  );
}

/**
 * `14 Nov 2026` from DRV-04's `2026-11-14`.
 *
 * Split rather than parsed. `new Date("2026-11-14")` is midnight **UTC**, and printing that through
 * the device's locale moves the date back a day for every courier west of Greenwich — a certificate
 * valid to the 14th drawn as valid to the 13th. `expiresOn` is a calendar date with no instant in
 * it, so it is formatted as one.
 *
 * The design prints this field two ways on one screen: `Valid to 14 Nov 2026` on the insurance row
 * and `Valid to Mar 2027` on the registration row. One field cannot have two formats, and the full
 * date is the one the mobile sweep cites and the one that loses nothing, so it is used for both
 * rows. The month-only form is recorded as Q-121 rather than guessed at.
 */
export function longDate(date: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (parts === null) return date;
  const month = MONTHS[Number(parts[2]) - 1];
  if (month === undefined) return date;
  return `${String(Number(parts[3]))} ${month} ${parts[1]!}`;
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * Whole days from today to a calendar date, which is what `Expires in 12 days` counts.
 *
 * Both ends are taken to UTC midnight before subtracting, so a day with a clock change in it is
 * still one day. `Math.floor` is not needed on a difference of two midnights and is not used, which
 * is deliberate: `nextExpiryDays` in `shift-readiness.ts` floors because its ends are *instants*,
 * and the two must not look like the same arithmetic.
 */
export function wholeDaysUntil(date: string, today: Date): number | undefined {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (parts === null) return undefined;
  const expiry = Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  if (!Number.isFinite(expiry)) return undefined;
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((expiry - now) / 86_400_000);
}

/**
 * Why `Upload new roadworthiness` cannot be carried out.
 *
 * The design's primary button, drawn because nothing may be removed, and honest about why pressing
 * it would not work. The upload itself is reachable — ADR 0025 gave FIL-02 a public surface and the
 * scope `uploads:self` for exactly this — and **attaching** what it uploads is not:
 * `PUT /v1/vehicles/{vehicleId}/documents/{documentId}` is guarded by `vehicle:document:write`
 * alone, with no self arm and no ownership test, so granting it to a courier's client would let that
 * courier rewrite the papers of every vehicle in the tenant.
 *
 * That is the same defect `delivery-proof:read` has (R-33) and the same one the vehicle **read** had
 * until ADR 0038 gave it a `vehicle:self` arm, and it is the reason this sentence names the scope
 * rather than the route: the route exists and works, and what is missing is a courier-shaped arm on
 * it. The subject to bound it by now exists — the assignment's `driverUserId` — and whether a
 * courier may file a document against the machine they are assigned is an operator's decision rather
 * than a shape nobody had, which is Q-136. `7 Document expiring`'s own
 * `Upload renewed permit` is in precisely this state against DRV-03, which is the precedent rather
 * than a coincidence.
 */
export const UPLOAD_DOCUMENT_UNAVAILABLE =
  "Uploading works, but recording the certificate against this vehicle needs vehicle:document:write — a fleet scope with no courier arm, so a courier's app holding it could rewrite every vehicle's papers.";

/**
 * Why `Add another vehicle` cannot be carried out.
 *
 * `POST /v1/vehicles/{vehicleId}/assignments` is exactly the right route and has exactly the right
 * arm: a courier holding `vehicle-assignment:self` may claim a vehicle into their own name, and a
 * body naming anybody else is refused. ADR 0034 built it for this button.
 *
 * What is missing is the `vehicleId` of the vehicle being added. The one this screen holds is the
 * one already assigned, and claiming it again is refused 409 — "a vehicle holds at most one active
 * assignment" — which is correct and is not what the button does. Registering a new vehicle is
 * `POST /v1/vehicles` under `vehicle:create`, a fleet scope; finding an existing unassigned one
 * would need a vehicle listing, and DRV-04 publishes none at all. So the button is drawn and says
 * this.
 *
 * The mobile sweep counts this element as answered, and correctly for the question it asks: the
 * route is reachable and the screen holds a `vehicleId`. The question it does not ask is *which*
 * vehicle, which is Q-124.
 */
export const ADD_ANOTHER_VEHICLE_UNAVAILABLE =
  "Claiming a vehicle works, but nothing published tells this app the new vehicle's id: POST /v1/vehicles needs vehicle:create and DRV-04 publishes no vehicle listing.";

/** What the screen says when the courier's assignment page came back empty, which is designed. */
export const NO_VEHICLE_TITLE = "No vehicle is yours yet";
export const NO_VEHICLE_SUB =
  "Nobody has assigned a vehicle to you, and this app can't claim one without being told which vehicle it is.";

/** What it says when every assignment has ended, which is a different fact from never having one. */
export const NO_ACTIVE_VEHICLE_SUB =
  "Your vehicle assignment has ended, so there is no vehicle to show its papers for.";

/** Said in place of the three document rows when the assignment read worked and the vehicle read did not. */
export const DOCUMENTS_UNREADABLE =
  "This vehicle's papers couldn't be read, so none of them is shown rather than any of them being shown as valid.";

function sentenceCase(value: string): string {
  return value.length === 0 ? value : value[0]!.toUpperCase() + value.slice(1);
}

function isPresent(value: string | undefined): value is string {
  return value !== undefined && value.length > 0;
}

/** `a`, `a and b`, `a, b and c` — the connector `proof.ts` uses, for the same sentences. */
function joinWithAnd(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]!}`;
}

/** Narrowed for the screen: what it needs in hand to draw the design, and what it could not get. */
export interface VehicleView {
  readonly assignment: VehicleAssignment;
  /** The vehicle behind the assignment, or undefined when `vehicle:self` did not answer. */
  readonly vehicle: VehicleRecord | undefined;
}
